import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Grid, Paper, Button, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { fetchData, postData }   from "../../BaseUrl";
import Toast                     from "../../DynamicComponent/Toast";
import BiliteracyEnrollmentForm  from "./BiliteracyEnrollmentForm";

/* ─── Static data ───────────────────────────────────────── */
const PROCESS_STEPS = [
  { step: 1, title: "Submit Application" },
  { step: 2, title: "Student prepares for Bi-literacy" },
  { step: 3, title: "Student assessed for Bi-literacy" },
  { step: 4, title: "Results in Term 3 end" },
];

const DEFAULT_ELIGIBILITY = [
  { label: "Grade: Nilai 8",         met: false },
  { label: "Overall Grade: A(92%)",  met: true  },
  { label: "Attendance: 96%",        met: true  },
];

/* ─── Component ─────────────────────────────────────────── */
const SealOfBiliteracy = () => {
  const [eligibility,      setEligibility]      = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [paying,           setPaying]           = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const applicationId = sessionStorage.getItem("applicationId") || "";

  /* ── Data loaders ── */
  const loadEligibility = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await fetchData(`student/biliteracy/eligibility/${applicationId}`);
      if (res?.data) setEligibility(res.data);
    } catch {
      setEligibility({ criteria: DEFAULT_ELIGIBILITY });
    }
  }, [applicationId]);

  const loadEnrollmentStatus = useCallback(async () => {
    if (!applicationId) return;
    try {
      const res = await fetchData(`student/biliteracy/status/${applicationId}`);
      if (res?.data) setEnrollmentStatus(res.data);
      // null / empty → not enrolled
    } catch { /* no enrollment yet — that's fine */ }
  }, [applicationId]);

  useEffect(() => {
    loadEligibility();
    loadEnrollmentStatus();
  }, [loadEligibility, loadEnrollmentStatus]);

  /* ── Pay Now handler ── */
  const handlePayNow = async () => {
    if (!applicationId) return;
    setPaying(true);
    try {
      const res = await postData(`student/biliteracy/pay/${applicationId}`, {});
      if (res?.success || res?.data) {
        setToast({ open: true, severity: "success", message: "Payment processed. Enrollment active!" });
        loadEnrollmentStatus();
      } else {
        setToast({ open: true, severity: "info", message: "Payment submitted for review." });
        loadEnrollmentStatus();
      }
    } catch {
      setToast({ open: true, severity: "error", message: "Payment failed. Please try again." });
    } finally {
      setPaying(false);
    }
  };

  /* ── Helpers ── */
  const handleFormClose  = () => loadEnrollmentStatus();
  const handleToastClose = () => setToast((p) => ({ ...p, open: false }));

  const eligibilityItems = eligibility?.criteria || DEFAULT_ELIGIBILITY;

  /* Check if enrolled: if enrollmentStatus has data with a real status, consider enrolled */
  const isEnrolled = enrollmentStatus && (
    enrollmentStatus.status === "pending" ||
    enrollmentStatus.status === "eligible" ||
    enrollmentStatus.status === "payment_completed" ||
    enrollmentStatus.enrolled === true
  );

  /* Map enrollment status → current process step */
  const currentStep = !enrollmentStatus ? 1
    : enrollmentStatus.status === "pending"           ? 2
    : enrollmentStatus.status === "eligible"          ? 3
    : enrollmentStatus.status === "payment_completed" ? 4
    : 1;

  /* ── Render ── */
  return (
    <Box sx={{ p: 1 }}>
      <Toast
        open={toast.open}
        handleClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />

      {/* ── Page header ── */}
      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontSize: "13.5px", fontWeight: 600 }}>
        Seal of Biliteracy
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.2, mb: 3 }}>
        Track your Seal of Biliteracy application status
      </Typography>

      <Grid container spacing={3}>

        {/* ── Left: Pre-enrollment card OR inline enrollment form ── */}
        <Grid item xs={12} md={8}>
          {!isEnrolled ? (
            /* Pre-enrollment state */
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 7,
                px: 4,
                textAlign: "center",
              }}
            >
              <HourglassEmptyIcon sx={{ fontSize: "72px", color: "#F59E0B", mb: 2 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#F59E0B", mb: 1 }}>
                Enroll Now
              </Typography>
              <Typography sx={{ fontSize: "15px", color: "#6B7280", mb: 3.5 }}>
                Seal of Biliteracy enrollment is open
              </Typography>
              <Button
                variant="contained"
                disabled={paying}
                onClick={handlePayNow}
                sx={{
                  backgroundColor: "#F59E0B",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "15px",
                  px: 5,
                  py: 1.2,
                  borderRadius: "10px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#D97706" },
                  "&.Mui-disabled": { backgroundColor: "#FCD34D", color: "#fff" },
                }}
              >
                {paying ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Pay Now"}
              </Button>
            </Paper>
          ) : (
            /* Already enrolled: show the form/view */
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <BiliteracyEnrollmentForm
                handleClose={handleFormClose}
                applicationId={applicationId}
                isInline
              />
            </Paper>
          )}
        </Grid>

        {/* ── Right: Eligibility + Process ── */}
        <Grid item xs={12} md={4}>

          {/* Your Eligibility card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              p: 2.5,
              mb: 2.5,
              border: "1px solid #E5E7EB",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 2 }}>
              Your Eligibility
            </Typography>

            {eligibilityItems.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: idx < eligibilityItems.length - 1 ? 1.5 : 0,
                  px: 2,
                  py: 1.1,
                  borderRadius: "8px",
                  backgroundColor: item.met ? "#DCFCE7" : "#FEE2E2",
                }}
              >
                {/* Icon circle */}
                <Box
                  sx={{
                    width: 22, height: 22, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: item.met ? "#22C55E" : "#EF4444",
                    flexShrink: 0,
                  }}
                >
                  {item.met
                    ? <CheckIcon sx={{ fontSize: "13px", color: "#fff" }} />
                    : <CloseIcon sx={{ fontSize: "13px", color: "#fff" }} />
                  }
                </Box>

                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: item.met ? "#166534" : "#991B1B",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Process card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              p: 2.5,
              border: "1px solid #E5E7EB",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827", mb: 2 }}>
              Process
            </Typography>

            {PROCESS_STEPS.map((s) => {
              const isActive = s.step === currentStep;
              return (
                <Box
                  key={s.step}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.1,
                    mb: 0.5,
                    borderRadius: "8px",
                    backgroundColor: isActive ? "#FEF3C7" : "transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: isActive ? 700 : 400,
                      color:      isActive ? "#92400E" : "#6B7280",
                    }}
                  >
                    {s.step}&nbsp; {s.title}
                  </Typography>
                </Box>
              );
            })}
          </Paper>

        </Grid>
      </Grid>
    </Box>
  );
};

export default SealOfBiliteracy;
